export interface AnamAvatar {
  id: string;
  personaId: string; // Actual Anam API persona ID
  name: string;
  avatarName: string; // Display name of the avatar character
  gender: 'female' | 'male' | 'unspecified';
  ageGroup: string;
  imageUrl: string; // Avatar preview image from Anam
  voiceName: string; // Voice used by this persona
}

export const ANAM_AVATARS: AnamAvatar[] = [
  // Female Avatars (sorted by age)
  {
    id: 'layla-30s',
    personaId: '95cf5c8f-cf58-4dd0-8fc9-9450f9557d1e',
    name: 'Layla — Female in 30s',
    avatarName: 'Layla',
    gender: 'female',
    ageGroup: '30s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/layla_home.png',
    voiceName: 'Emma',
  },
  {
    id: 'evelyn-50s',
    personaId: '1fc21309-422b-4c86-a67d-29e76f1a8a56',
    name: 'Evelyn — Female in 50s',
    avatarName: 'Evelyn',
    gender: 'female',
    ageGroup: '50s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/evelyn_desk.png',
    voiceName: 'Irene',
  },
  {
    id: 'julia-70s',
    personaId: '491a6110-0eae-483b-923c-e907eea1675c',
    name: 'Julia — Female in 70s',
    avatarName: 'Julia',
    gender: 'female',
    ageGroup: '70s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/julia_sofa.png',
    voiceName: 'Julia',
  },
  // Male Avatars (sorted by age)
  {
    id: 'kevin-20s',
    personaId: '5b7d254f-0897-49f1-b5d2-84bed1453381',
    name: 'Kevin — Male in 20s-30s',
    avatarName: 'Kevin',
    gender: 'male',
    ageGroup: '20s-30s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/kevin_table.png',
    voiceName: 'James (Australian)',
  },
  {
    id: 'finn-30s',
    personaId: 'c46ed74a-ada2-4fe5-a138-7f7dfe56895f',
    name: 'Finn — Male in 30s',
    avatarName: 'Finn',
    gender: 'male',
    ageGroup: '30s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/finn_lean.png',
    voiceName: 'Charlie (Australian)',
  },
  {
    id: 'finn-30s-alt',
    personaId: 'd3e88616-45b2-4b6e-b299-f28e125571dd',
    name: 'Finn — Male in 30s (Alt Voice)',
    avatarName: 'Finn',
    gender: 'male',
    ageGroup: '30s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/finn_lean.png',
    voiceName: 'James (Australian)',
  },
  {
    id: 'dr-ross-50s',
    personaId: '994042fa-3b27-441e-8909-26cebcd06ca3',
    name: 'Dr Ross Geller — Male Doctor in 50s',
    avatarName: 'Dr Ross Geller',
    gender: 'male',
    ageGroup: '50s',
    imageUrl: 'https://newgxnc1uqs0jnqm.public.blob.vercel-storage.com/avatar-previews/aPpUvmwtV5pQr3F6Fv_QlQbeK9sYriQ-/one-shot_aPpUvmwtV5pQr3F6Fv_QlQbeK9sYriQ-_one-shot-1ldct231760678459824-cropped-GVANrjMLnsEjiGCGdABpHIP9SlWVBa.png',
    voiceName: 'Hugh (British)',
  },
  {
    id: 'gabriel-50s',
    personaId: '527ef46f-b531-4225-af2a-6c2bd0bf27e3',
    name: 'Gabriel — Male in 50s',
    avatarName: 'Gabriel',
    gender: 'male',
    ageGroup: '50s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/gabriel_table.png',
    voiceName: 'Jarnathan',
  },
  {
    id: 'john-peterson-60s',
    personaId: '0e1e4052-8e85-4bb3-b250-18892e21b808',
    name: 'Mr John Peterson — Male in 60s',
    avatarName: 'Mr John Peterson',
    gender: 'male',
    ageGroup: '60s',
    imageUrl: 'https://newgxnc1uqs0jnqm.public.blob.vercel-storage.com/avatar-previews/aPpUvmwtV5pQr3F6Fv_QlQbeK9sYriQ-/one-shot_aPpUvmwtV5pQr3F6Fv_QlQbeK9sYriQ-_one-shot-jji8wz1761099294187-cropped-apmgWknNXifIiclSMyVF6ylnk1tXYl.png',
    voiceName: 'William (Australian)',
  },
  {
    id: 'william-80s',
    personaId: 'af47db90-ff2d-411f-ba9f-0d55cae8c1c1',
    name: 'William — Male in 80s',
    avatarName: 'William',
    gender: 'male',
    ageGroup: '80s',
    imageUrl: 'https://lab.anam.ai/persona_thumbnails/william_lean.png',
    voiceName: 'William',
  },
];

// Helper functions
export function getAvatarsByGender(gender: 'female' | 'male'): AnamAvatar[] {
  return ANAM_AVATARS.filter(avatar => avatar.gender === gender);
}

export function getAvatarByPersonaId(personaId: string): AnamAvatar | undefined {
  return ANAM_AVATARS.find(avatar => avatar.personaId === personaId);
}

export function getAvatarOptionLabel(
  avatar: AnamAvatar,
  catalogue: AnamAvatar[] = ANAM_AVATARS,
): string {
  const genderAvatars = catalogue.filter(candidate => candidate.gender === avatar.gender);
  const optionNumber = genderAvatars.findIndex(candidate => candidate.id === avatar.id) + 1;
  const genderLabel = avatar.gender === 'female'
    ? 'Female'
    : avatar.gender === 'male'
      ? 'Male'
      : 'Additional';

  return `${genderLabel} avatar ${Math.max(optionNumber, 1)}`;
}

export function isKnownAvatarGender(
  gender: AnamAvatar['gender'],
): gender is 'female' | 'male' {
  return gender === 'female' || gender === 'male';
}
